import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	ClearMessageFiles,
	CreateConversation,
	CreateConversationSuccess,
	CreateMessage,
	CreateMessageSuccess,
	EMessagesActions,
	GetConversations,
	GetConversationsSuccess,
	GetMessages,
	GetMessagesSuccess,
	LeaveConversation,
	LeaveConversationSuccess,
	PostMessageFiles,
	PostMessageFilesSuccess,
	PushMessageToConversation,
	RenameConversation,
	RenameConversationSuccess,
} from '@app/core/store/actions/messages.actions';
import {MessagesService} from '@app/core/services/api/messages/messages.service';
import {Conversation} from '@app/core/models/conversation.model';
import {Message} from '@app/core/models/message.model';
import {FileEntryService} from '@app/modules/shared/services/fileEntry.service';
import {FileEntry} from '@app/core/models/fileEntry.model';
import {getCurrentState} from '@app/core/helpers';
import {IAppState} from '@app/core/store/state/app.state';
import {Store} from '@ngrx/store';

@Injectable()
export class MessagesEffects {

	@Effect()
	public onGetConversations$ = this.actions$.pipe(
		ofType<GetConversations>(EMessagesActions.GetConversations),
		switchMap((action: GetConversations) => from(this.messageService.getConversations()).pipe(
			switchMap((conversations: Conversation[]) => [
				new GetConversationsSuccess(conversations),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPushMessageToConversation$ = this.actions$.pipe(
		ofType<PushMessageToConversation>(EMessagesActions.PushMessageToConversation),
		switchMap((action: PushMessageToConversation) => {
			const conversationIsInState = getCurrentState(this.store).messages.conversations.find(c => c.id === action.conversation.id);
			if (conversationIsInState) {
				return [];
			}
			return [
				new GetConversations(),
			];
		}),
	);

	@Effect()
	public onGetMessages$ = this.actions$.pipe(
		ofType<GetMessages>(EMessagesActions.GetMessages),
		switchMap((action: GetMessages) => from(this.messageService.getMessages(action.id, action.page_size, action.page)).pipe(
			switchMap((res: any) => [
				new GetMessagesSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onCreateMessage$ = this.actions$.pipe(
		ofType<CreateMessage>(EMessagesActions.CreateMessage),
		switchMap((action: CreateMessage) => from(this.messageService.createMessage(action.id, action.data)).pipe(
			switchMap((message: Message) => [
				new CreateMessageSuccess(message),
				new ClearMessageFiles(),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onCreateConversation$ = this.actions$.pipe(
		ofType<CreateConversation>(EMessagesActions.CreateConversation),
		switchMap((action: CreateConversation) => from(this.messageService.createConversation(action.data, action.conversationName)).pipe(
			switchMap((conversation: Conversation) => [
				new CreateConversationSuccess(conversation),
				new CreateMessage(conversation.id, action.message)
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPostMessageFiles$ = this.actions$.pipe(
		ofType<PostMessageFiles>(EMessagesActions.PostMessageFiles),
		switchMap((action: PostMessageFiles) => from(this.fileEntryService.uploadFiles(action.files)).pipe(
			switchMap((fileEntries: FileEntry[]) => [
				new PostMessageFilesSuccess(fileEntries)
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onLeaveConversation$ = this.actions$.pipe(
		ofType<LeaveConversation>(EMessagesActions.LeaveConversation),
		switchMap((action: LeaveConversation) => from(this.messageService.leaveConversation(action.id)).pipe(
			switchMap(() => [
				new LeaveConversationSuccess(action.id),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onRenameConversation$ = this.actions$.pipe(
		ofType<RenameConversation>(EMessagesActions.RenameConversation),
		switchMap((action: RenameConversation) => from(this.messageService.renameConversation(action.data, action.id)).pipe(
			switchMap((conversation: Conversation) => [
				new RenameConversationSuccess(conversation),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private actions$: Actions,
		private messageService: MessagesService,
		private fileEntryService: FileEntryService,
		private store: Store<IAppState>,
	) {
	}
}
